import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import { Crop, Sun, Contrast, RotateCcw, Check, X, Sparkles } from 'lucide-react';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop, brightness, contrast, filterStyle) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  let filter = `brightness(${brightness}%) contrast(${contrast}%)`;
  if (filterStyle) filter += ` ${filterStyle}`;
  ctx.filter = filter;

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

const FILTERS = [
  { id: 'none', name: 'Original', css: '' },
  { id: 'bw', name: 'B/N', css: 'grayscale(100%)' },
  { id: 'sepia', name: 'Sepia', css: 'sepia(80%)' },
  { id: 'warm', name: 'Cálido', css: 'sepia(30%) saturate(130%)' },
  { id: 'cool', name: 'Frío', css: 'hue-rotate(30deg) saturate(80%)' },
  { id: 'vintage', name: 'Vintage', css: 'sepia(40%) contrast(90%) brightness(95%)' },
  { id: 'vivid', name: 'Vívido', css: 'saturate(160%) contrast(110%)' },
  { id: 'dramatic', name: 'Drama', css: 'contrast(130%) brightness(90%) saturate(80%)' },
];

function ImageEditor({ imageSrc, onSave, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [activeFilter, setActiveFilter] = useState('none');
  const [activeTab, setActiveTab] = useState('crop');
  const [saving, setSaving] = useState(false);
  const brightnessRef = useRef(null);
  const contrastRef = useRef(null);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const currentFilter = FILTERS.find(f => f.id === activeFilter) || FILTERS[0];

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, brightness, contrast, currentFilter.css);
      onSave(blob);
    } catch (e) {
      console.error('Error al procesar imagen:', e);
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    setBrightness(100);
    setContrast(100);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setActiveFilter('none');
  };

  const combinedFilter = [
    `brightness(${brightness}%)`,
    `contrast(${contrast}%)`,
    currentFilter.css
  ].filter(Boolean).join(' ');

  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" data-testid="image-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-neutral-900 border-b border-neutral-800 flex-shrink-0">
        <button onClick={onCancel} className="text-neutral-400 hover:text-white p-2" data-testid="editor-cancel">
          <X className="h-6 w-6" />
        </button>
        <div className="flex bg-neutral-800 rounded-lg p-0.5 gap-0.5">
          {[
            { id: 'crop', icon: Crop, label: 'Recortar' },
            { id: 'adjust', icon: Sun, label: 'Ajustar' },
            { id: 'filters', icon: Sparkles, label: 'Filtros' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === tab.id ? 'bg-white text-black' : 'text-neutral-400 hover:text-white'
              }`} data-testid={`tab-${tab.id}`}>
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
        <button onClick={handleSave} disabled={saving}
          className="bg-green-600 hover:bg-green-500 text-white p-2 rounded-lg disabled:opacity-50"
          data-testid="editor-save">
          <Check className="h-6 w-6" />
        </button>
      </div>

      {/* Cropper area */}
      <div className="flex-1 relative overflow-hidden" style={{ minHeight: 0 }}>
        <div className="absolute inset-0" style={{ filter: combinedFilter }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={undefined}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={activeTab === 'crop'}
            minZoom={0.5}
            maxZoom={4}
          />
        </div>
      </div>

      {/* Controls */}
      <div className="bg-neutral-900 border-t border-neutral-800 flex-shrink-0 safe-area-bottom">
        {activeTab === 'crop' && (
          <div className="px-4 py-4 space-y-3">
            <div className="flex items-center gap-3">
              <Crop className="h-4 w-4 text-neutral-500 flex-shrink-0" />
              <span className="text-neutral-400 text-xs w-10">Zoom</span>
              <input type="range" min="50" max="400" step="5" value={zoom * 100}
                onChange={e => setZoom(parseInt(e.target.value) / 100)}
                className="flex-1 h-8 accent-white"
                data-testid="slider-zoom" />
              <span className="text-neutral-500 text-xs w-10 text-right">{zoom.toFixed(1)}x</span>
            </div>
            <p className="text-neutral-600 text-[11px] text-center">
              Pellizca para zoom. Arrastra para mover.
            </p>
          </div>
        )}

        {activeTab === 'adjust' && (
          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center gap-3">
              <Sun className="h-4 w-4 text-neutral-500 flex-shrink-0" />
              <span className="text-neutral-400 text-xs w-16">Brillo</span>
              <input ref={brightnessRef} type="range" min="50" max="150" step="1" value={brightness}
                onChange={e => setBrightness(parseInt(e.target.value))}
                className="flex-1 h-8 accent-yellow-400"
                data-testid="slider-brightness" />
              <span className="text-neutral-500 text-xs w-10 text-right">{brightness}%</span>
            </div>
            <div className="flex items-center gap-3">
              <Contrast className="h-4 w-4 text-neutral-500 flex-shrink-0" />
              <span className="text-neutral-400 text-xs w-16">Contraste</span>
              <input ref={contrastRef} type="range" min="50" max="150" step="1" value={contrast}
                onChange={e => setContrast(parseInt(e.target.value))}
                className="flex-1 h-8 accent-blue-400"
                data-testid="slider-contrast" />
              <span className="text-neutral-500 text-xs w-10 text-right">{contrast}%</span>
            </div>
            <button onClick={resetAll}
              className="w-full flex items-center justify-center gap-2 py-2 text-neutral-500 hover:text-white text-xs transition-colors"
              data-testid="btn-reset">
              <RotateCcw className="h-3.5 w-3.5" /> Restablecer todo
            </button>
          </div>
        )}

        {activeTab === 'filters' && (
          <div className="px-3 py-3">
            <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
              {FILTERS.map(f => (
                <button key={f.id} onClick={() => setActiveFilter(f.id)}
                  className={`flex-shrink-0 text-center transition-all ${
                    activeFilter === f.id ? 'scale-105' : 'opacity-70'
                  }`} data-testid={`filter-${f.id}`}>
                  <div className={`w-16 h-16 rounded-lg overflow-hidden border-2 mb-1 ${
                    activeFilter === f.id ? 'border-white' : 'border-transparent'
                  }`}>
                    <img src={imageSrc} alt={f.name}
                      className="w-full h-full object-cover"
                      style={{ filter: f.css || 'none' }} />
                  </div>
                  <span className={`text-[10px] ${activeFilter === f.id ? 'text-white font-medium' : 'text-neutral-500'}`}>
                    {f.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageEditor;
